import { useState } from 'react';

/**
 * FAQ, visible on the landing page AND mirrored as FAQPage JSON-LD in index.html
 * (keep the two in sync). Part of making this page itself AIO-optimised, and a
 * source of citable, answer-first content (eat our own dogfood). Brand name
 * "PageCoder" / "pagecoder.ai" appears naturally so AI associates the entity.
 */
/**
 * Company entity facts. Stating the founding year and country strongly helps AI
 * cite the right organisation, and mirrors the Organization schema (foundingDate,
 * address) in index.html (#3). The brand PageCoder is the cited entity.
 */
export const COMPANY = {
  country: 'Slovakia',
  /** The person who builds and runs this. PageCoder.ai is one person, not a team (D94). */
  person: 'Jan Michalík',
  /** Legal entity, one spelling. It invoices; it is never the headline identity. */
  legal: 'MEDIALINK s.r.o.',
};

export const FAQ_ITEMS: { q: string; a: string }[] = [
  {
    q: 'What is AI Visibility Scan?',
    /*
      Two claims, kept apart on purpose.

      Whether an AI can REACH and READ a page is checked against eleven named
      crawlers, because that is a property of the page and its robots.txt.
      Whether an AI actually CITES you is only measured where a question is put
      to a live model, and that is GPT and Gemini. Writing "checks whether
      ChatGPT, Perplexity, Claude and Gemini can cite you" would claim four
      measurements where there are two.
    */
    a: 'AI Visibility Scan is a free tool that checks whether AI can reach, read and cite your website. The reading side is checked against the crawlers that feed AI answers, including GPTBot and OAI-SearchBot (ChatGPT), ClaudeBot, PerplexityBot and Google-Extended (Gemini and AI Overviews). The citing side is measured live, by putting real questions to GPT and Gemini with web search and recording whether the answer names you. This is called GEO or AEO, and the tool shows exactly what to fix.',
  },
  {
    q: 'Who is behind AI Visibility Scan?',
    a: `AI Visibility Scan is built and run by ${COMPANY.person} (PageCoder.ai, ${COMPANY.country}). PageCoder.ai is one person, not a studio or an agency, and invoices through ${COMPANY.legal}. I also build RAG Chat, an on-site AI answer tool. I scan only the public URLs you enter, use no tracking cookies, and never inflate a score to sell a fix.`,
  },
  {
    q: 'Is PageCoder a company or a person?',
    a: `A person. ${COMPANY.person}, working from ${COMPANY.country}, builds and runs everything under PageCoder.ai and answers support himself. ${COMPANY.legal} is the legal entity that invoices, nothing more. If you write to contact@pagecoder.ai, I read it.`,
  },
  {
    q: 'What is GEO / AEO / AIO?',
    a: 'GEO (Generative Engine Optimization) and AEO (Answer Engine Optimization) are the practice of getting your brand cited in AI-generated answers. At PageCoder I use the shorthand AIO. Classic SEO decides your position in blue links. AIO decides whether AI mentions you in its answer, a new layer of visibility.',
  },
  {
    q: 'How is AI Visibility Scan different from SEO tools?',
    a: 'SEO checkers tell you whether structured data exists. AI Visibility Scan goes further: a real AI is asked to read your page and report what it can actually extract and cite, and real web-search queries are run to see whether AI names you in answers or names competitors instead. That second part is reality, not a prediction.',
  },
  {
    q: 'Can I be cited by AI with perfect on-page optimization alone?',
    a: 'No. You can have a perfectly optimised page and still never be cited if no one else on the internet talks about you. The strongest signals AI uses are off-site: brand mentions, links, and presence in knowledge bases such as Wikipedia, Wikidata and Crunchbase. On-page work makes you eligible; off-site reputation decides it. Anyone guaranteeing AI mentions from on-page tweaks alone is selling hype.',
  },
  {
    q: 'Is AI Visibility Scan free?',
    /*
      No figure in this sentence.

      The price is admin-editable (D-25, revised by D-31), and it was written out
      here as "€20 per month". Change it in admin and this answer starts lying,
      in the one place a visitor goes to check. The Pricing section renders the
      live number from the same source the order form uses.
    */
    a: 'Yes. The diagnosis is free: the Readiness score, the findings, and a limited Visibility and Footprint check. The full prioritised Fix it action plan and larger scans are in PageCoder Pro, and the current price is on the Pricing section of this page. Everything the tool recommends is also something you can do yourself for free.',
  },
  {
    q: 'Is it included with PageCoder RAG Chat?',
    a: 'Take RAG Chat for six months or more and your first month of AI Visibility Scan Pro is free. After that month Pro is billed normally if you want to keep it, and the free scan stays free either way. RAG Chat makes AI answer questions on your own site; AI Visibility Scan makes AI cite you across the wider web. They are two halves of the same visibility loop.',
  },
  {
    q: 'Which AI models does it check?',
    a: 'The readability/extraction test runs on a compact model (GPT-class, with a Claude fallback). The live Visibility and Footprint checks run on your choice of Gemini or GPT, both with live web search. Which engines are available is admin-controlled, so PageCoder only offers what it can actually deliver.',
  },
  {
    q: 'Is my data safe?',
    a: 'PageCoder scans only the public URL you enter. There are no tracking cookies and no third-party sharing. The report lives online in your dashboard, and email is opt-in only. PageCoder is EU-based and GDPR-compliant by design.',
  },
  {
    q: 'How do I improve my AI visibility?',
    a: 'Run a scan, then follow the PageCoder Fix it plan in order. First, let AI read you: unblock crawlers and make sure content is in the HTML. Second, sharpen the page: add Organization schema, sameAs, a clear entity and quotable facts. Third, publish answers to the real buyer questions as an FAQ with FAQPage schema. Fourth, build off-site presence: knowledge bases, YouTube and channels, earned mentions. Then re-scan to see "absent" turn into "mentioned".',
  },
];

function Item({ q, a }: { q: string; a: string }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-warm-dark-2">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between gap-3 py-4 text-left">
        <span className="text-text-primary">{q}</span>
        <span className="text-cyan font-mono text-lg shrink-0">{open ? '−' : '+'}</span>
      </button>
      {open && <p className="text-sm text-text-secondary pb-4 -mt-1 leading-relaxed">{a}</p>}
    </div>
  );
}

/**
 * `embedded` means this is a dashboard page rather than the tail of the scan
 * page. The page head has already said what it is, and the generous top margin
 * and rule exist to separate it from the scan above it, which is not there.
 */
export default function Faq({ embedded = false }: { embedded?: boolean } = {}): JSX.Element {
  return (
    <section className={embedded
      ? 'w-full max-w-2xl'
      : 'w-full max-w-2xl mx-auto mt-20 pt-8 border-t border-warm-dark-2'}>
      {!embedded && (
        <>
          <h2 className="font-serif text-2xl mb-2 text-center">Frequently asked questions</h2>
          <p className="text-sm text-text-secondary text-center mb-8">
            About AI visibility, GEO and AEO, and how the PageCoder tool works.
          </p>
        </>
      )}
      <div>
        {FAQ_ITEMS.map((it) => (
          <Item key={it.q} q={it.q} a={it.a} />
        ))}
      </div>
    </section>
  );
}
